import { clsx } from 'clsx';
import { AppLink } from '@/components/ui/AppLink';
import type { LinkProps } from 'react-router';
import { SITE_NAME } from '@/lib/config';
import { useT } from '@/lib/i18n';

/**
 * The SBG INDIA logo — the owner's artwork (`scripts/brand/sbg-logo.png`, red letters and tilak on a
 * transparent background, D-326, replaced by the owner's flat red version in D-339), shown straight on the dark site. The files are made by
 * `scripts/gen-pwa-icons.ts` and served from `/logo` (not imported, so the bundle stays as small as it was).
 * Decorative: the name is on the link that wraps it.
 */
export function BrandLogo({
  className,
  sizes = '168px',
}: {
  className?: string;
  /** What the browser should assume about the width the image is shown at, for picking a file. */
  sizes?: string;
}) {
  return (
    <span className={clsx('brand-logo', className)}>
      <img
        className="brand-logo__img"
        src="/logo/sbg-logo-320.webp"
        srcSet="/logo/sbg-logo-160.webp 160w, /logo/sbg-logo-320.webp 320w, /logo/sbg-logo-640.webp 640w"
        sizes={sizes}
        width={320}
        height={196}
        alt=""
        decoding="async"
        loading="lazy" /* React would hoist an eager <img> into <head> as a preload, ahead of <title> */
        draggable={false}
      />
    </span>
  );
}

/**
 * The logo as a link home (header, footer, mobile menu). In the header and the mobile menu the firm's name is written
 * just below the logo (the owner asked for it, D-329, and for bigger type, D-331) — it is for the eye only: the link's own name
 * ("Shree Balaji Granites — home") already says it. The footer shows a larger logo with the name in larger type (D-331), then the tagline.
 */
export function Wordmark({
  className,
  large = false,
  caption = !large,
  ...props
}: Omit<LinkProps, 'to' | 'children'> & { large?: boolean; caption?: boolean }) {
  const t = useT();
  return (
    <AppLink
      to="/"
      className={clsx('wordmark', !caption && 'wordmark--plain', className)}
      aria-label={t('nav.homeLink', { brand: SITE_NAME })}
      {...props}
    >
      <BrandLogo
        className={large ? 'wordmark__logo wordmark__logo--lg' : 'wordmark__logo'}
        sizes={large ? '192px' : '132px'}
      />
      {caption ? (
        <span className="wordmark__caption" aria-hidden="true">
          {SITE_NAME}
        </span>
      ) : null}
    </AppLink>
  );
}
